import type fr from '../fr/site';

const en: typeof fr = {
  nav: {
    services: 'Services',
    how: 'How it works',
    quote: 'Quote',
    sectors: 'Areas',
    faq: 'FAQ',
    about: 'About',
    login: 'Log in',
    cta: 'Request a quote',
    myAccount: 'My account',
    menu: 'Menu',
    language: 'Change language',
    otherLanguage: 'Français',
    home: 'Ménage Éclat home',
  },
  meta: {
    homeTitle: 'Ménage Éclat · Residential and commercial cleaning, South Shore and Montréal',
    homeDescription: 'Reliable residential and commercial cleaning on Montréal’s South Shore and in Montréal. Online quote, the same team, track your visits from your client space.',
    servicesTitle: 'Our cleaning services',
    servicesDescription: 'Residential, recurring, deep cleaning, move in/out, commercial and custom services.',
    faqTitle: 'Frequently asked questions',
    faqDescription: 'Presence, keys, pets, changes, cancellation, pricing: answers about our cleaning service.',
    sectorsTitle: 'Areas we serve',
    sectorsDescription: 'The South Shore and Montréal areas our teams travel to.',
    quoteTitle: 'Get my quote',
    quoteDescription: 'Describe your property in a few steps and get an instant estimate.',
    aboutTitle: 'About',
    aboutDescription: 'A stable, caring team and simple tools to follow your service.',
  },
  hero: {
    eyebrow: 'South Shore and Montréal',
    title: 'A clean home, without thinking about it.',
    subtitle: 'Reliable residential and commercial cleaning on the South Shore and in Montréal. Book simply, follow your visits and manage your service from your client space.',
    cta: 'Request a quote',
    login: 'Log in',
    services: 'See our services',
    imageAlt: 'Bright, tidy living room with a white sofa and green plants',
    nextTitle: 'Next cleaning',
    nextWhen: 'Tuesday 10:00',
    nextTeam: 'Team Julie & Marie',
    doneTitle: 'Cleaning done',
    doneText: 'All tasks completed',
  },
  trust: {
    team: { title: 'Reliable team', text: 'The same people, visit after visit' },
    insured: { title: 'Dependable service', text: 'Instructions respected and tracked' },
    booking: { title: 'Easy booking', text: 'Online quote in a few minutes' },
    tracking: { title: 'Online tracking', text: 'Schedule, messages and history' },
  },
  services: {
    eyebrow: 'Our services',
    title: 'Solutions for every need',
    more: 'Request this service',
    all: 'See all services',
    residential: {
      title: 'Residential cleaning',
      text: 'Complete upkeep of your house, condo or apartment.',
      b1: 'Every room, floors to surfaces',
      b2: 'Checklist followed every visit',
      b3: 'Your instructions remembered',
      alt: 'Bright living room with a large window',
    },
    recurring: {
      title: 'Recurring cleaning',
      text: 'Weekly, every two weeks or monthly.',
      b1: 'Same team whenever possible',
      b2: 'Discount based on frequency',
      b3: 'Every visit can be changed',
      alt: 'Bright bedroom with a made bed',
    },
    deep: {
      title: 'Deep cleaning',
      text: 'A thorough clean: grout, baseboards, under furniture.',
      b1: 'Kitchen and bathrooms in depth',
      b2: 'Often-forgotten details',
      b3: 'Ideal at the change of seasons',
      alt: 'Kitchen sink being wiped with a cloth',
    },
    move: {
      title: 'Move in / out',
      text: 'Empty home cleaned before you move in or after you leave.',
      b1: 'Inside cabinets',
      b2: 'Appliances',
      b3: 'Ready for key handover',
      alt: 'Moving boxes in a living room',
    },
    commercial: {
      title: 'Commercial cleaning',
      text: 'Offices, clinics and shops, on your schedule.',
      b1: 'Outside your opening hours',
      b2: 'Tailored frequency',
      b3: 'Secure access instructions',
      alt: 'Bright office space with plants',
    },
    custom: {
      title: 'Custom services',
      text: 'Oven, fridge, windows, bed linen, basement, cabinets.',
      b1: 'À la carte options',
      b2: 'Special requests noted',
      b3: 'Price confirmed in your quote',
      alt: 'Gloved hand wiping a surface',
    },
  },
  how: {
    eyebrow: 'How it works',
    title: 'A simple experience in 4 steps',
    s1: { title: 'Describe your property', text: 'Type, rooms, pets and preferences, just once.' },
    s2: { title: 'Receive your quote', text: 'An instant estimate, then a price confirmed by our team.' },
    s3: { title: 'Choose your schedule', text: 'The date, time and frequency that suit you.' },
    s4: { title: 'We take care of the rest', text: 'Your team arrives prepared, you follow everything online.' },
  },
  app: {
    eyebrow: 'Your client space',
    title: 'Manage everything from your space',
    text: 'Your service in one place, on your phone or your computer.',
    f1: 'Next cleaning and team',
    f2: 'Calendar of your visits',
    f3: 'Instructions and preferences',
    f4: 'Messages with our team',
    f5: 'Your property profile',
    f6: 'Online payments (coming soon)',
    f7: 'Cleaning history',
    previewLabel: 'Client space preview',
    previewHello: 'Hello Marie',
    previewNext: 'Next cleaning',
    previewDate: 'Tuesday, Sept. 10',
    previewTime: '10:00 · 2 h 30',
    previewTeam: 'Julie & Marie',
    previewUpcoming: 'Upcoming visits',
    previewQuote: 'Quote to accept',
    previewQuoteText: 'Deep cleaning',
    previewMessage: 'Julie: “Noted about the dog.”',
    previewChecklist: 'Checklist',
  },
  why: {
    eyebrow: 'Why us',
    title: 'Why choose us?',
    stable: { title: 'Stable team', text: 'We favour continuity so the same people get to know your home.' },
    instructions: { title: 'Instructions remembered', text: 'Pets, priorities, parking and special requests stay attached to your property.' },
    simple: { title: 'Easy to manage', text: 'Change your visits and check your schedule from your phone.' },
    clear: { title: 'Clear tracking', text: 'You know when your cleaning is scheduled and when it is done.' },
  },
  reviews: {
    eyebrow: 'Reviews',
    title: 'What our clients say',
    empty: 'No reviews published yet.',
    emptyHint: 'Our clients’ reviews will appear here as soon as they are published.',
    demoBadge: 'Demo',
    demoNote: 'Demo examples, shown only in development mode.',
    d1: { name: 'Demo client A', text: 'Example review: punctual team, spotless home.' },
    d2: { name: 'Demo client B', text: 'Example review: very handy to follow everything online.' },
    d3: { name: 'Demo client C', text: 'Example review: the same team every visit.' },
  },
  sectors: {
    eyebrow: 'Areas we serve',
    title: 'Close to you',
    text: 'Our teams travel to the following areas. Not listed? Request a quote anyway.',
    empty: 'The list of areas will be published soon.',
    mapAlt: 'Schematic map of the areas we serve',
  },
  quoteBand: {
    title: 'Get your quote in a few minutes',
    text: 'A few questions about your property, an instant estimate, then a price confirmed by our team.',
    cta: 'Get my quote',
  },
  faq: {
    eyebrow: 'FAQ',
    title: 'Frequently asked questions',
    more: 'See all questions',
    q1: { q: 'Do I need to be home during the cleaning?', a: 'No. Add your access instructions (door code, lockbox, key, concierge) to your property profile. They are encrypted and only visible to the assigned team around the visit.' },
    q2: { q: 'Can I have the same team?', a: 'We favour continuity: assignment prefers people who already know your home. Depending on availability, another team member may be assigned; you will see it in your space.' },
    q3: { q: 'What should I prepare?', a: 'Put away loose personal items so the team can clean the surfaces. Add to your profile the rooms not to enter, your priorities and products to use or avoid.' },
    q4: { q: 'Do you need a key?', a: 'Not necessarily. Choose a door code, a lockbox, a key handed over, concierge access or another instruction, all stored securely.' },
    q5: { q: 'What about my pets?', a: 'Add them to your profile with your instructions. The team is informed before every visit and the planned time takes them into account.' },
    q6: { q: 'Can I change my appointment?', a: 'Yes, right from your client space up to {{cutoff}} before the visit. After that, write to us in the messages and we will do our best.' },
    q7: { q: 'How far ahead must I cancel?', a: 'You can cancel online up to {{cutoff}} before the scheduled time. After that, contact us through the messages.' },
    q8: { q: 'How are prices set?', a: 'By property type, number of bedrooms and bathrooms, floors, square footage, pets, type of cleaning, options and frequency (discount for regular service). The online estimate is indicative; the official price is the one in the quote you accept.' },
    q9: { q: 'Can I request specific tasks?', a: 'Yes: choose options such as inside the oven or fridge, interior windows or bed linen, and add your special requests when booking.' },
    q10: { q: 'Do you offer recurring service?', a: 'Yes: weekly, every two weeks, monthly or a custom interval. Each visit can still be changed on its own and you can pause the service.' },
  },
  about: {
    eyebrow: 'About',
    title: 'A team that makes the difference',
    text: 'We believe great cleaning is built on trust: stable people who know your home, instructions respected and clear communication.',
    p1: 'Teams that come back to your home',
    p2: 'Instructions kept in your profile',
    p3: 'A checklist followed every visit',
    imageAlt: 'Smiling cleaner holding a broom in a kitchen',
  },
  footer: {
    tagline: 'A clean home, without thinking about it.',
    company: 'Company',
    services: 'Services',
    help: 'Help',
    legal: 'Legal',
    contact: 'Contact',
    privacy: 'Privacy policy',
    terms: 'Terms of use',
    cancellation: 'Cancellation policy',
    rights: 'All rights reserved.',
  },
  quote: {
    title: 'Get my quote',
    subtitle: 'A few questions, an instant estimate. No commitment.',
    stepOf: 'Step {{n}} of {{total}}',
    steps: { property: 'Property type', details: 'Your home', service: 'Service and frequency', summary: 'Summary' },
    propertyQuestion: 'What type of property?',
    detailsQuestion: 'Tell us about your home',
    serviceQuestion: 'Which service, and how often?',
    summaryQuestion: 'Your estimate',
    bedrooms: 'Bedrooms',
    bathrooms: 'Bathrooms',
    floors: 'Floors',
    pets: 'There are pets in the home',
    service: 'Type of cleaning',
    frequency: 'Frequency',
    extras: 'Options (optional)',
    back: 'Back',
    next: 'Next',
    loading: 'Calculating your estimate…',
    range: 'Between {{low}} and {{high}}',
    perVisit: 'per visit, plus taxes',
    duration: 'Estimated duration: {{duration}}',
    disclaimer: 'Indicative estimate. The official price is confirmed by our team in your quote.',
    error: 'We could not calculate the estimate. Please try again.',
    retry: 'Try again',
    continue: 'Get my official quote',
    continueHint: 'Create your account (or log in): your answers are kept.',
    choose: 'Choose an option to continue.',
    summaryProperty: 'Property',
    summaryService: 'Service',
    rooms: '{{bedrooms}} bed · {{bathrooms}} bath · {{floors}} floor(s)',
    withPets: 'with pets',
    decrease: '{{label}}: remove one',
    increase: '{{label}}: add one',
  },
  legal: {
    draft: 'Content to be validated before going live. This text is a template and is not legal advice.',
    updated: 'Last updated: {{date}}',
    privacy: {
      title: 'Privacy policy',
      intro: 'We protect the personal information you entrust to us, in accordance with Québec’s Act respecting the protection of personal information in the private sector (Law 25).',
      s1: { h: 'Information collected', p: 'Name, email, phone, property addresses, home characteristics, preferences, access instructions, visit history, messages and payments.' },
      s2: { h: 'Use', p: 'To plan and perform cleanings, communicate with you, bill and improve the service. We do not sell your information.' },
      s3: { h: 'Access instructions', p: 'Codes, keys and alarm instructions are encrypted. Only authorized people can see them, and only around the visit; every view is logged.' },
      s4: { h: 'Retention and hosting', p: 'Data is hosted in Canada and kept as long as needed for these purposes and legal obligations.' },
      s5: { h: 'Your rights', p: 'You may request access, correction or deletion of your information, and withdraw consent, by writing to us.' },
      s6: { h: 'Person in charge', p: 'The person in charge of protecting personal information can be reached at the company contact details at the bottom of this page.' },
    },
    terms: {
      title: 'Terms of use',
      intro: 'These terms govern the use of the website and the client space.',
      s1: { h: 'Account', p: 'You are responsible for the accuracy of your information and the confidentiality of your password.' },
      s2: { h: 'Quotes and prices', p: 'Online estimates are indicative. The applicable price is the one in the accepted quote.' },
      s3: { h: 'Appointments', p: 'Appointments can be changed or cancelled online according to the cancellation policy.' },
      s4: { h: 'Property access', p: 'You agree to provide safe access and accurate instructions.' },
      s5: { h: 'Liability', p: 'Report any issue within 48 hours after the visit through the messages in your space.' },
    },
    cancellation: {
      title: 'Cancellation policy',
      intro: 'We understand that things come up.',
      s1: { h: 'Notice', p: 'You can change or cancel a cleaning online up to {{cutoff}} before the scheduled time.' },
      s2: { h: 'After the deadline', p: 'Write to us through the messages. A late cancellation or no-show may incur fees, stated in your quote.' },
      s3: { h: 'Recurring service', p: 'Each visit can be changed on its own. You can pause or cancel the service from your space, according to the commitment in your agreement.' },
    },
  },
  cutoffHours: '{{count}} hours',
  cutoffDays_one: '{{count}} business day',
  cutoffDays_other: '{{count}} business days',
};
export default en;
